import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {VelocityTransitionGroup} from 'velocity-react';
import {actions, select} from './connectors';

import Animations from '~/constants/Animations';
import DesignIconConstants from '~/constants/DesignIconConstants';
import {DIRECTION_LEFT, DIRECTION_RIGHT} from '~/constants/HammerConstants';
import Keys from '~/constants/Keyboard';
import MediaQueries, {breakpoint} from '~/constants/MediaQueries';
import {VIEW_ID_MAP} from '~/constants/ProductConstants';
import {YOUR_IMAGE_HERE_IDS} from '~/constants/YourImageHereConstants';
import {getViewName} from '~/utils/catalog';
import {findIcon, isNamesOrNumbers} from '~/data/accessors/icon';
import Color from '~/data/records/Color';
import {keyIsModified, targetIsInput} from '~/utils/keyboard';
import Canvas from '~/shared/components/Canvas';
import CanvasToolbar from '~/components/CanvasToolbar';
import MobileToolbar from '~/components/MobileToolbar';
import MobileBottomToolbar from '~/components/MobileBottomToolbar';
import DesignNote from '~/components/DesignNote';
import RedoUndoToolbar from '~/components/RedoUndoToolbar';
import RealTimePriceDecider from '~/components/RealTimePrice/Decider';
import ProductNote from '~/components/ProductNote';
import ProductNoteMobile from '~/components/ProductNoteMobile';
import ProductWarningMobile from '~/components/ProductWarningMobile';
import withRouter, {routerPropTypes} from '~/router/withRouter';
import {matchPath} from 'react-router-dom';
import ElevateMobileUpload from '~/components/MainMenuBar/ElevateMobileUpload';

import {getLowResolutionWarning} from '~/selectors/CanvasSelectors';

import './index.scss';

const propTypes = {
  handleAfterOffClick: PropTypes.func.isRequired,
  activateIntersectingIconButton: PropTypes.func,
  anchorProduct: PropTypes.object,
  backward: PropTypes.func,
  blankDesign: PropTypes.bool,
  blur: PropTypes.bool,
  center: PropTypes.func,
  changeView: PropTypes.func,
  contextualToolsOpen: PropTypes.bool,
  copy: PropTypes.func,
  cut: PropTypes.func,
  deactivateIntersectingIconButton: PropTypes.func,
  defaultIconColor: PropTypes.object,
  deselectAllIcons: PropTypes.func,
  designName: PropTypes.string,
  disabled: PropTypes.bool,
  duplicate: PropTypes.func,
  fetchingDesign: PropTypes.bool,
  fetchStyle: PropTypes.func,
  forward: PropTypes.func,
  getProductViews: PropTypes.func,
  getViewConstraint: PropTypes.func,
  guidelines: PropTypes.arrayOf(
    PropTypes.shape({
      height: PropTypes.number.isRequired,
      left: PropTypes.number.isRequired,
      top: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      name: PropTypes.string
    })
  ),
  handleInvalidProductImage: PropTypes.func,
  hasClipping: PropTypes.bool,
  hasOverlay: PropTypes.bool,
  hasSleeveDesign: PropTypes.bool,
  hasSleeveProductImageIssue: PropTypes.bool,
  iconRendered: PropTypes.func,
  icons: PropTypes.arrayOf(PropTypes.object),
  intersectingIcons: PropTypes.object,
  isCircularEmbroideryEnabled: PropTypes.bool,
  isCurrentDesignInCart: PropTypes.bool,
  isEmbroidered: PropTypes.bool,
  isFundraisingMode: PropTypes.bool,
  isLarge: PropTypes.bool,
  isMultiItemEligible: PropTypes.bool,
  isSleeveDecoEligible: PropTypes.bool,
  move: PropTypes.func,
  navigateToCrop: PropTypes.func,
  notifyError: PropTypes.func,
  paste: PropTypes.func,
  product: PropTypes.object,
  productColor: PropTypes.instanceOf(Color),
  prompt: PropTypes.func,
  redo: PropTypes.func,
  redoable: PropTypes.bool,
  remove: PropTypes.func,
  repositionForTextEffects: PropTypes.func,
  resize: PropTypes.func,
  router: routerPropTypes,
  selectedIconId: PropTypes.string,
  selectedIconIds: PropTypes.arrayOf(PropTypes.string),
  selectedIconType: PropTypes.string,
  selectedView: PropTypes.shape({
    constraint: PropTypes.object,
    id: PropTypes.number,
    printScaleFactor: PropTypes.number
  }),
  selectedViewId: PropTypes.number,
  selectIcon: PropTypes.func,
  selectIcons: PropTypes.func,
  showTools: PropTypes.bool,
  similarProductWithImages: PropTypes.func,
  sleevePrintEnabled: PropTypes.bool,
  undo: PropTypes.func,
  undoable: PropTypes.bool,
  updateFontHeight: PropTypes.func,
  updateLockRatio: PropTypes.func,
  uploadOrBrowseArt: PropTypes.func,
  zoomIn: PropTypes.func,
  zoomOut: PropTypes.func,
  onClick: PropTypes.func
};

/** @extends {Component<PropTypes.InferProps<typeof propTypes>>} */
export class CanvasContainer extends Component {
  static displayName = 'CanvasContainer';
  static propTypes = propTypes;

  state = {
    isCurrentDesignInCart: false,
    isZoomed: false,
    showOverlay: true,
    showProductNote: true
  };

  componentDidMount() {
    this._keyHandler = this.getKeyHandler();
    window.addEventListener('keydown', this._keyHandler);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.product &&
      nextProps.product &&
      this.props.product.id !== nextProps.product.id
    ) {
      this.setState({isZoomed: false});
    }

    if (this.props.showTools !== nextProps.showTools) {
      this.setState({showProductNote: nextProps.showTools});
    }
  }

  shouldComponentUpdate(nextProps) {
    return !!nextProps.product && !nextProps.product.errors;
  }

  componentDidUpdate() {
    if (this.props.selectedView.id !== this.props.selectedViewId) {
      this.props.changeView(this.props.selectedView.id);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this._keyHandler);
  }

  getSelectedIcons = () => {
    return this.props.icons.filter(icon =>
      this.props.selectedIconIds.includes(icon.id)
    );
  };

  getIconsInView = () => {
    if (!this.props.selectedView) {
      return [];
    }

    return this.props.icons.filter(
      icon => this.props.selectedView.id === icon.viewId
    );
  };

  handleZoomToggle = () => {
    this.setState(prevState => ({isZoomed: !prevState.isZoomed}));
    if (this.state.isZoomed) {
      this.props.zoomOut();
    } else {
      this.props.zoomIn();
    }
  };

  handleRemoveSelected = () => {
    const selectedIcons = this.getSelectedIcons() || [];
    if (!selectedIcons.length) {
      return;
    }
    const isNamesOrNumbersRemoved = selectedIcons.some(icon =>
      isNamesOrNumbers(icon.type)
    );

    if (isNamesOrNumbersRemoved && this.props.isLarge) {
      this.props.router.navigate('/namesAndNumbers/tools');
    } else {
      this.props.router.navigate('/');
    }

    this.props.remove({
      iconIds: selectedIcons.map(icon => icon.id),
      iconTypes: selectedIcons.map(icon => icon.type)
    });
  };

  handlePaste = () => {
    const {selectedView} = this.props;
    this.props.paste({
      viewId: selectedView.id,
      viewConstraint: selectedView.constraint
    });

    if (breakpoint(MediaQueries.largeAndAbove)) {
      setTimeout(() => {
        const lastPastedIcon = this.props.icons.find(
          icon => icon.id === this.props.selectedIconId
        );

        if (lastPastedIcon) {
          this.handleOpenIconTools(lastPastedIcon);
        }
      });
    }
  };

  getKeyHandler = () => {
    const keyHandlers = {
      [Keys.C]: {
        handle: () => this.props.copy(),
        validate: () => !!this.props.selectedIconId
      },
      [Keys.S]: {
        handle: evt => this.props.router.navigate('/next/saveForm'),
        validate: evt => keyIsModified(evt)
      },
      [Keys.V]: {
        handle: this.handlePaste,
        validate: evt => keyIsModified(evt) && this.props.hasClipping
      },
      [Keys.X]: {
        handle: evt => {
          this.props.cut();
          this.props.router.navigate('/');
        },
        validate: evt => keyIsModified(evt) && !!this.props.selectedIconId
      },
      [Keys.Z]: {
        handle: evt => (evt.shiftKey ? this.redo() : this.undo()),
        validate: evt => keyIsModified(evt)
      },
      [Keys.BACKSPACE]: {
        handle: evt => this.handleRemoveSelected()
      },
      [Keys.DELETE]: {
        handle: evt => this.handleRemoveSelected()
      }
    };

    return evt => {
      const keyHandler = keyHandlers[evt.key];
      if (
        !targetIsInput(evt) &&
        keyHandler &&
        (keyHandler.validate ? keyHandler.validate(evt) : true)
      ) {
        evt.preventDefault();
        keyHandler.handle(evt);
      }
    };
  };

  deselectIcon = () => {
    this.props.deselectAllIcons();
  };

  toggleView = viewId => {
    if (this.props.selectedView.id !== viewId) {
      this.props.router.navigate('/');
      this.props.changeView(viewId, {
        source: 'canvas_toolbar'
      });
    }
  };

  toggleOverlayHandler = () => {
    this.setState({showOverlay: !this.state.showOverlay});
  };

  handleFontResize = fontHeight => {
    const {icons, selectedIconId, updateFontHeight} = this.props;
    const {icon: selectedIcon} = findIcon(icons, selectedIconId);
    return updateFontHeight(selectedIconId, fontHeight, selectedIcon.type);
  };

  productDetails = () => this.props.router.navigate('/productDetails');

  isMultiItemStartFromDesign = () => {
    const {isMultiItemEligible, isCurrentDesignInCart, designName, router} =
      this.props;
    return (
      matchPath('/startFromDesign/', router.location.pathname),
      isMultiItemEligible &&
        isCurrentDesignInCart &&
        designName &&
        breakpoint(MediaQueries.mediumAndBelow)
    );
  };

  handleSelectIcon = (icon, toggle) => {
    const {nextSelectedIcons} = this.props.selectIcon({
      iconId: icon.id,
      toggle
    });

    this.resolveIconTools(nextSelectedIcons);
  };

  handleSelectIcons = icons => {
    const iconIds = icons.map(i => i.id);

    this.props.selectIcons({iconIds});
    this.resolveIconTools(icons);
  };

  resolveIconTools = selectedIcons => {
    const {router, selectedViewId, uploadOrBrowseArt} = this.props;

    if (selectedIcons.length) {
      if (
        selectedIcons.length === 1 &&
        breakpoint(MediaQueries.largeAndAbove)
      ) {
        this.handleOpenIconTools(selectedIcons[0]);
      } else if (
        selectedIcons.length === 1 &&
        YOUR_IMAGE_HERE_IDS.includes(selectedIcons[0].clipartId)
      ) {
        uploadOrBrowseArt({label: 'swap image'});
        router.navigate('/selectIcon/artAndUploadTools');
      } else if (selectedIcons[0].viewId !== selectedViewId) {
        this.deselectIcon();
        router.navigate('/');
      } else if (selectedIcons.length === 1) {
        switch (selectedIcons[0].type) {
          case DesignIconConstants.TYPES.clipart:
            router.navigate('/artTools');
            break;
          case DesignIconConstants.TYPES.text:
            router.navigate('/textTools');
            break;
          case DesignIconConstants.TYPES.upload:
            router.navigate('/uploadTools');
            break;
          default:
            router.navigate('/selectIcon');
        }
      } else {
        router.navigate('/selectIcon');
      }
    } else {
      router.navigate('/');
    }
  };

  handleSelectAll = () => this.handleSelectIcons(this.getIconsInView());

  selectText = () => {
    if (!matchPath('/textTools', this.props.router.location.pathname)) {
      this.props.router.navigate('/textTools');
    }
  };

  selectArt = () => {
    if (!matchPath('/artTools', this.props.router.location.pathname)) {
      this.props.router.navigate('/artTools');
    }
  };

  selectUpload = () => {
    if (!matchPath('/uploadTools', this.props.router.location.pathname)) {
      this.props.router.navigate('/uploadTools');
    }
  };

  selectUploadOrBrowseArt = () => {
    if (!matchPath('/artAndUploadTools', this.props.router.location.pathname)) {
      this.props.router.navigate('/artAndUploadTools');
    }
  };

  selectPers = () => {
    const namesAndNumbersSubMenus = location.href.match(
      /(\/namesAndNumbers\/tools\/(colors?|location|height))/
    );
    if (namesAndNumbersSubMenus) {
      const path = namesAndNumbersSubMenus[1];
      this.props.router.navigate(`${path}`);
    } else {
      this.props.router.navigate('/namesAndNumbers/tools');
    }
  };

  handleOpenIconTools = icon => {
    switch (icon.type) {
      case 'TEXT':
        this.selectText();
        break;
      case 'CLIPART': {
        if (YOUR_IMAGE_HERE_IDS.includes(String(icon.clipartId))) {
          this.props.uploadOrBrowseArt({label: 'swap image'});
          this.selectUploadOrBrowseArt();
        } else {
          this.selectArt();
        }
        break;
      }
      case 'NAMES':
      case 'NUMBERS':
        this.selectPers();
        break;
      case 'UPLOAD':
        this.selectUpload();
        break;
      default:
        throw new Error('Unrecognized icon type selected');
    }
  };

  handleAfterOffClick = evt => {
    this.props.handleAfterOffClick(evt);
    this.deselectIcon();
  };

  get unselectedViews() {
    const {product, selectedView} = this.props;
    const selectedViewId = selectedView && selectedView.id;
    return (
      product &&
      this.props
        .getProductViews(product)
        .filter(view => view.id !== selectedViewId)
    );
  }

  getProductViews = () => {
    const anchorViews = this.props.getProductViews(this.props.anchorProduct);
    return this.props.getProductViews(this.props.product).filter(view => {
      return !!anchorViews.find(v => v.id === view.id);
    });
  };

  handleViewChange = changeView => {
    const {selectedIconId} = this.props;

    if (selectedIconId && this.deselectIcon) {
      this.deselectIcon();
    }
    this.toggleView(changeView);
  };

  handleViewClick = targetView => {
    const {selectedIconId} = this.props;
    // prevent view changes when an icon is selected (small only)
    if (selectedIconId && !breakpoint(MediaQueries.mediumAndAbove)) {
      return;
    }
    this.handleViewChange(targetView.id);
  };

  handleViewSwipe = e => {
    // prevent view changes when an icon is selected (small only)
    if (this.props.selectedIconId && !breakpoint(MediaQueries.mediumAndAbove)) {
      return;
    }
    const selectedViewId = this.props.selectedView.id;
    const views = this.getProductViews();
    const viewIndex = views.findIndex(view => view.id === selectedViewId);

    if (e.direction === DIRECTION_RIGHT) {
      if (viewIndex !== 0) {
        const nextLeftSideView = views[viewIndex - 1];
        this.handleViewChange(nextLeftSideView.id);
      } else {
        const lastView = views[views.length - 1];
        this.handleViewChange(lastView.id);
      }
    } else if (e.direction === DIRECTION_LEFT) {
      if (viewIndex !== views.length - 1) {
        const nextRightSideView = views[viewIndex + 1];
        this.handleViewChange(nextRightSideView.id);
      } else {
        const firstView = views[0];
        this.handleViewChange(firstView.id);
      }
    }
  };

  undo = () => {
    const {undo} = this.props;
    undo()?.then(() => {
      const {icon: selectedIcon} = findIcon(
        this.props.icons,
        this.props.selectedIconId
      );
      if (!selectedIcon) {
        this.props.router.navigate('/');
      }
    });
  };

  redo = () => {
    const {redo} = this.props;
    redo();
  };

  renderToolsMenu() {
    const {icon: selectedIcon} = findIcon(
      this.props.icons,
      this.props.selectedIconId
    );

    let actions = {
      undo: this.undo,
      redo: this.redo
    };

    let actives = {
      undo: this.props.undoable,
      redo: this.props.redoable
    };

    actions = {...actions, changeView: this.handleViewChange};
    actives = {...actives, selectedViewId: this.props.selectedView.id};

    const minimumConstraintDim = 120;
    const views = this.getProductViews();
    const changeViews = views.map(view => {
      return {
        onClick: () => this.handleViewClick(view),
        image: (
          <div className="ndx-CanvasContainer-changeView">
            <Canvas
              constraint={this.props.getViewConstraint(view)}
              currentColorId={this.props.productColor.id}
              guidelines={view.guidelines}
              handleInvalidProductImage={this.props.handleInvalidProductImage}
              icons={this.props.icons}
              isSleeveDecoEligible={this.props.isSleeveDecoEligible}
              minimumConstraintHeight={minimumConstraintDim}
              minimumConstraintWidth={minimumConstraintDim}
              product={this.props.product}
              productColor={this.props.productColor}
              selectedView={view}
              similarProductWithImages={this.props.similarProductWithImages}
            />
          </div>
        ),
        text: getViewName(view.name, breakpoint(MediaQueries.largeAndAbove)),
        active: view.id === this.props.selectedView.id,
        id: view.id
      };
    });
    const changeViewMobileToolbar = views.map(view => {
      return {
        image: (
          <div className="ndx-CanvasContainer-changeView">
            <Canvas
              constraint={this.props.getViewConstraint(view)}
              currentColorId={this.props.productColor.id}
              guidelines={view.guidelines}
              handleInvalidProductImage={this.props.handleInvalidProductImage}
              icons={this.props.icons}
              isSleeveDecoEligible={this.props.isSleeveDecoEligible}
              minimumConstraintHeight={minimumConstraintDim}
              minimumConstraintWidth={minimumConstraintDim}
              product={this.props.product}
              productColor={this.props.productColor}
              selectedView={view}
              similarProductWithImages={this.props.similarProductWithImages}
            />
          </div>
        ),
        text: getViewName(view.name, true),
        id: view.id
      };
    });
    const acceptableViews = [VIEW_ID_MAP.front, VIEW_ID_MAP.back];
    const productViewIds = views.map(view => view.id);
    const hasAcceptableViews =
      productViewIds.length === acceptableViews.length &&
      productViewIds.every(productViewID =>
        acceptableViews.includes(productViewID)
      );

    return (
      <div className="ndx-CanvasContainer-toolbar">
        {breakpoint(MediaQueries.largeAndAbove) && (
          <CanvasToolbar
            actions={actions}
            actives={actives}
            additionalPrintArea={this.props.product?.additional_print_areas}
            product={this.props.product}
            changeViews={changeViews}
            designIsBlank={this.props.blankDesign}
            hasOverlay={this.props.hasOverlay}
            showOverlay={this.state.showOverlay}
            isEmbroidered={this.props.isEmbroidered}
            isFundraisingMode={this.props.isFundraisingMode}
            isZoomed={this.state.isZoomed}
            selectedIcon={selectedIcon}
            sleevePrintEligible={this.props.isSleeveDecoEligible}
            sleevePrintEnabled={this.props.sleevePrintEnabled}
            toggleOverlay={this.toggleOverlayHandler}
            zoomIn={this.handleZoomToggle}
            hasSleeveProductImageIssue={this.props.hasSleeveProductImageIssue}
          />
        )}

        {breakpoint(MediaQueries.largeAndAbove) ? (
          <RedoUndoToolbar actions={actions} actives={actives} />
        ) : (
          <MobileToolbar
            actions={actions}
            actives={actives}
            hasAcceptableViews={hasAcceptableViews}
            changeViews={changeViewMobileToolbar}
            additionalPrintArea={this.props.product.additional_print_areas}
            isSleeveDecoEligible={this.props.isSleeveDecoEligible}
            sleevePrintEnabled={this.props.sleevePrintEnabled}
            hasSleeveProductImageIssue={this.props.hasSleeveProductImageIssue}
          />
        )}
        {breakpoint(MediaQueries.mediumAndBelow) && <MobileBottomToolbar />}
        {breakpoint(MediaQueries.mediumAndBelow) &&
          this.props.icons?.length === 0 && <ElevateMobileUpload />}
      </div>
    );
  }

  handleProductNoteClose = () => {
    if (this.state.showProductNote) {
      this.setState({showProductNote: false});
    }
  };

  handleProductNoteToggle = () => {
    this.setState(prev => ({
      showProductNote: !prev.showProductNote
    }));
  };

  renderDesignNote() {
    const {designName} = this.props;
    if (this.isMultiItemStartFromDesign()) {
      return <DesignNote designName={designName} />;
    }
  }

  renderProductNote() {
    return breakpoint(MediaQueries.mediumAndBelow) ? (
      <div className="ndx-CanvasContainer-alertContainer">
        <ProductNoteMobile
          handleToggle={this.handleProductNoteToggle}
          showProductNote={this.state.showProductNote}
        />
        <ProductWarningMobile
          getWarning={getLowResolutionWarning}
          onHasWarning={this.handleProductNoteClose}
        />
      </div>
    ) : (
      <ProductNote
        handleProductNoteClose={this.handleProductNoteClose}
        showProductNote={this.state.showProductNote}
      />
    );
  }

  displayRealTimePrice() {
    return (
      this.props.isLarge &&
      !this.props.isFundraisingMode &&
      !this.props.showTools
    );
  }

  renderCanvas() {
    const {isCircularEmbroideryEnabled, isEmbroidered, selectedView} =
      this.props;
    const constraint = this.props.getViewConstraint(selectedView);
    const iconsInView = this.getIconsInView();

    return (
      <div
        id="canvas-container"
        className="ndx-CanvasContainer"
        onClick={this.props.onClick}>
        {this.props.product && (
          <>
            <Canvas
              activateIntersectingIconButton={
                this.props.activateIntersectingIconButton
              }
              deactivateIntersectingIconButton={
                this.props.deactivateIntersectingIconButton
              }
              intersectingIcons={this.props.intersectingIcons}
              backward={this.props.backward}
              blankDesign={this.props.blankDesign}
              blur={this.props.blur}
              center={this.props.center}
              constraint={constraint}
              copy={this.props.copy}
              currentColorId={this.props.productColor?.id}
              cut={this.props.cut}
              defaultIconColor={this.props.defaultIconColor}
              deselectIcon={this.deselectIcon}
              duplicate={this.props.duplicate}
              disabled={!!this.props.onClick || this.props.disabled}
              fetchingDesign={this.props.fetchingDesign}
              forward={this.props.forward}
              guidelines={this.props.guidelines}
              handleAfterOffClick={this.handleAfterOffClick}
              handleOpenIconTools={this.handleOpenIconTools}
              handleSelectIcon={this.handleSelectIcon}
              handleSelectIcons={this.handleSelectIcons}
              handleInvalidProductImage={this.props.handleInvalidProductImage}
              hasClipping={this.props.hasClipping}
              iconRendered={this.props.iconRendered}
              icons={this.props.icons}
              iconsInView={iconsInView}
              interactive
              isCircularEmbroideryTestEnabled={
                isCircularEmbroideryEnabled && isEmbroidered
              }
              isSleeveDecoEligible={this.props.isSleeveDecoEligible}
              isZoomed={this.state.isZoomed}
              main
              move={this.props.move}
              navigateToCrop={this.props.navigateToCrop}
              paste={this.handlePaste}
              product={this.props.product}
              productColor={this.props.productColor}
              remove={this.handleRemoveSelected}
              repositionForTextEffects={this.props.repositionForTextEffects}
              resize={this.props.resize}
              selectAll={this.handleSelectAll}
              selectedIconId={this.props.selectedIconId}
              selectedIconIds={this.props.selectedIconIds}
              selectedIconType={this.props.selectedIconType}
              selectedView={this.props.selectedView}
              similarProductWithImages={this.props.similarProductWithImages}
              showOverlay={this.props.hasOverlay && this.state.showOverlay}
              updateFontSize={this.handleFontResize}
            />
          </>
        )}
        <VelocityTransitionGroup
          enter={Animations.fadeIn}
          leave={Animations.fadeOut}>
          {this.props.product && this.props.showTools && this.renderToolsMenu()}
        </VelocityTransitionGroup>
        {this.displayRealTimePrice() && <RealTimePriceDecider />}
        {this.renderProductNote()}
        {this.renderDesignNote()}
      </div>
    );
  }

  render() {
    return this.renderCanvas();
  }
}

export default connect(select, dispatch => actions(dispatch))(
  withRouter(CanvasContainer)
);
